<template>
  <full-page-loader :loading="isLoading"/>
  <layout-wrapper>
    <template #header>
      <van-nav-bar left-arrow @click-left="$router.back()">
        <template #title>
          <div v-show="!productNameIsVisible">{{ data?.name }}</div>
        </template>
      </van-nav-bar>
    </template>

    <template v-if="hasProduct">
      <div class="bg-white pb-20">
        <client-only>
          <gallery :key="galleryRefetchKey" :data="data" :images="images"/>
        </client-only>

        <div class="flex justify-between items-center pt-1 pb-2 px-3">
          <nuxt-link :to="$routerUrl(data?.manufacturer_seo_url)">
            <niceone-image :src="data?.manufacturer_image" width="60"/>
          </nuxt-link>
          <div class="flex-center flex-col py-4">
            <product-features :id="id" :data="data"
                              :selected-option="selectedOptionsAttributes.defaultOption"/>
          </div>
        </div>

        <Three-sixty-small-devices :data="data"/>
        <Image-comparison-small-devices :data="data"/>

        <div class="px-3 my-2">
          <nuxt-link :to="$routerUrl(data?.manufacturer?.trim()?.toLowerCase()?.split(' ').join('-'))">
            <span class="pe-2 text-[14px] text-color-[#73be22]">
              {{ $t("product.original_100") }}
            </span>
            <span class="text-color-[#5436a3]">
              {{ $t("product.click_here_to_see_more_from_the_same_brand") }}
            </span>
          </nuxt-link>
        </div>
        <van-divider/>

        <h1 ref="productNameRef" class="mb-2 px-3 text-lg">{{ data?.name }}</h1>

        <product-attributes
            v-if="data?.has_option"
            :product="data"
            :selectedOptionsAttributes="selectedOptionsAttributes"
            @reset="$resetProductAttributes"
        >
          <template #default-option>
            <product-options
                v-model="selectedOptionsAttributes.defaultOption"
                :options="data.options"
                :placeholder="$t('product.reviews.please_select_option')"
                @change="galleryRefetchKey++"
            />
          </template>
          <template #second-option>
            <product-options
                v-model="selectedOptionsAttributes.secondaryOption"
                :options="data.options"
                :placeholder="$t('product.reviews.please_select_option')"
                @change="galleryRefetchKey++"
            />
          </template>
        </product-attributes>

        <div class="flex items-center px-3 pt-3">
          <span class="text-color-[#f5c523] text-[14px]">{{ data.rating }}</span>
          <van-rate
              v-model="data.rating"
              allow-half
              class="px-2"
              color="#f5c523"
              readonly
              size="16"
              void-color="#eee"
              void-icon="star"
          />
          <span class="text-[12px]">
            {{ $t("product.reviews.reviews", {review_total: reviewsData?.review_total}) }}
          </span>
        </div>

        <installment-widgets :data="data" class="my-3 mx-3 pt-3"/>
        <div class="mb-3 px-3 pt-3">
          <span class="flex items-end gap-3">
            <price :increase-font-by="4" :min-height="0" :product="data"
                   :selected-option="selectedOptionsAttributes.defaultOption"/>
            <points-tag :data="data" :selected-option="selectedOptionsAttributes"/>
            <product-tags-pdp :selected-option="selectedOptionsAttributes.defaultOption" :tag="data?.tag"/>
          </span>
          <span v-if="data?.taxText" class="text-color-[#797979] text-[12px]">
            {{ data?.taxText }}
          </span>
          <!-- <marketing-banner class="mt-2"/> -->
        </div>
        <loyalty-tags :data="data" :selected-option="selectedOptionsAttributes" class="px-3"/>
        <infos :items="data?.infos"/>

        <estimated-time class="mb-3"/>
        <template v-if="data?.in_offer">
          <pdp-offers :id="id" :selected-option="selectedOptionsAttributes"/>
        </template>
        <template v-if="!isLoading">
          <template
              v-if="data.related_products && data.related_products.length"
          >
            <related-products
                :id="id"
                :data="data"
                :products="data.related_products"
                :selected-option="selectedOptionsAttributes.defaultOption"
                class="m-3"
            />
          </template>

          <div class="inherit-style product-details">
            <product-details :product="data"/>
          </div>

          <div class="bg-light-grey">
            <reviews-section
                :data="data"
                :hide-attachments="true"
                :reviews="reviews"
                :reviews-data="reviewsData"
            />
          </div>

          <!-- Cart button  -->
          <sticky-bottom-button>
            <template #button>
              <cart-button
                  :id="id"
                  :data="data"
                  :selected-option="selectedOptionsAttributes.defaultOption"
                  :selectedOptionsAttributes="selectedOptionsAttributes"
                  :source-data="setEventsData(data)"
              />
            </template>
          </sticky-bottom-button>

          <!-- Recommended Products  -->
          <div class="bg-light-grey p-3">
            <product-list-grid-section
                :data="{
                productId: data.id,
                category: parseInt(data.category_id),
              }"
                :source-data="EVENT_SOURCES.RELATED_PRODUCTS_SECTION">
            </product-list-grid-section>
          </div>
        </template>

        <!-- Products you may like -->
        <like-products/>
      </div>
    </template>

    <template v-if="noProductFound">
      <error/>
    </template>
  </layout-wrapper>
</template>

<script setup>
import StickyBottomButton from "@/components/common/StickyBottomButton";
import ProductDetails from "@/components/product/Details";
import Price from "@/components/product/Price";
import Gallery from "@/components/product/gallery/mobile";
import ProductListGridSection from "@/components/product/ProductListGridSection";
import {useIntersectionObserver} from "@vueuse/core";
import ProductOptions from "@/components/product/Options";
import Infos from "@/components/product/Infos";
import LoyaltyTags from "@/components/product/loyalty/";
import PointsTag from "@/components/product/loyalty/PointsTag"
import CartButton from "@/components/product/ProductDetailsCartButton";
import RelatedProducts from "@/components/product/RelatedProducts";
import LikeProducts from "@/components/product/LikeProductsSection";
import ReviewsSection from "@/components/product/ReviewsSection";
import InstallmentWidgets from "@/components/product/installment-widgets/index";
import ProductFeatures from "@/components/product/ProductFeatures";
import PdpOffers from "@/components/product/offers/index";
import error from "@/components/error";
import {TRACKERS, EVENT_SOURCES} from "@/constants/events";
import {track} from "@/composables/useTrackEvent";
import {useCartStore} from "@/store/cart";
import ProductJsonld from "@/jsonld/product";
import ProductHead from "@/head/product";
import {useCustomFetch} from "@/composables/useFetch";
import ProductTagsPdp from "@/components/tags/ProductTagsPDP";
import EstimatedTime from "@/components/product/EstimatedTime.vue";
import {useSeoUrlRedirection} from "@/composables/useValidLinkRedirection"
import {BLACKLISTED_PRODUCTS_IDS, SCHEMA_POSITIONED_PRODUCTS} from "@/constants";
import {useProductUrlParams} from "@/composables/useProductUrlParams";
import ThreeSixtySmallDevices from "@/components/product/ThreeSixtySmallDevices";
import ImageComparisonSmallDevices from "@/components/product/ImageComparisonSmallDevices";

const {selectedOptionsAttributes} = storeToRefs(useCartStore());
const {$resetProductAttributes} = useCartStore();
const route = useRoute();

const {id, optionId} = useProductUrlParams()

const preview = computed(() => route.query?.preview)

const images = computed(() => {
  const defaultOption = selectedOptionsAttributes.value.defaultOption;

  if (!defaultOption?.image) {
    return data.value?.images ? [...data.value.images] : [];
  }

  //correct order for images
  return [
    defaultOption.image,
    ...(defaultOption.additional_images || []),
    ...(data.value?.images || []),
  ];
});


const galleryRefetchKey = ref(1);

const params = {
  id,
  ...(preview.value && {preview})
};

// Get product details
const {data, loading: isLoading} = await useCustomFetch(
    "?route=rest/product_admin/products",
    {
      id,
      preview
    }
);

await useSeoUrlRedirection(data.value?.seo_url, optionId)

const hasProduct = computed(() => {
  if (BLACKLISTED_PRODUCTS_IDS.includes(Number(id))) return false
  return !isLoading.value && data.value?.id
})

const noProductFound = computed(() => {
  if (BLACKLISTED_PRODUCTS_IDS.includes(Number(id))) return true
  return !isLoading.value && !data.value?.id
})

// Get product reviews
const {data: reviewsData, loading: loading} = await useCustomFetch(
    "?route=rest/reviews/product_reviews",
    {
      id,
      offset: 1,
      limit: 3,
    }
);

const reviews = computed(() => reviewsData.value?.reviews || []);

if (SCHEMA_POSITIONED_PRODUCTS.includes(Number(id))) {
  // Json schema
  useJsonld(
      ProductJsonld({product: data.value, productReviews: reviewsData.value})
  );
}

const {t} = useI18n();
// Head schema
useHead({
  titleTemplate: (name) => t("seo.product_title_template", {name}),
  title: () => data.value?.name,
  ...ProductHead({product: data.value}),
});

if (!SCHEMA_POSITIONED_PRODUCTS.includes(Number(id))) {
  // Json schema
  useJsonld(
      ProductJsonld({product: data.value, productReviews: reviewsData.value})
  );
}

const productNameRef = ref(null);
const productNameIsVisible = ref(false);

// Intersection observer to watch the visibility of the product name
const {stop} = useIntersectionObserver(
    productNameRef,
    ([{isIntersecting}]) => {
      productNameIsVisible.value = isIntersecting;
    }
);

const selectProductOptionFromUrl = () => {

  if (!optionId) return

  // Select the option based on optionId from the URL
  const selectedOption = data.value?.has_option && data.value?.options?.[0]?.option_value?.find(item => Number(item.product_option_variant_id) === Number(optionId))
  selectedOptionsAttributes.value.defaultOption = selectedOption || null
}

watchEffect(() => {
  if (data.value) {
    track(TRACKERS.PRODUCT_DETAILS_VIEWED, data.value);
    selectProductOptionFromUrl()
  }
})

onBeforeUnmount(() => {
  stop(); // Stop the Intersection Observer when the component is unmounted
});

const setEventsData = (product) => {
  return {...EVENT_SOURCES.PRODUCT_DETAILS_PAGE, source_name: product?.en_name, source_id: product.id, quick_add: false}
}
</script>